import { useCallback, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { fetchRequirementsLoads, fetchTruckFilterLoads } from "@/services/loadFinderApi";
import { useLoadFinderStore } from "@/state/loadFinderStore";

const TRUCK_FILTER_LOADS_KEY = ["loadFinder", "truckFilterLoads"];
const REFRESH_INTERVAL_MS = 60000;
const STALE_TIME_MS = 0;

export const requirementsLoadsQueryKey = (params) => ["loadFinder", "requirementsLoads", params];

export const toErrorMessage = (error) => {
  if (!error) return null;
  if (typeof error === "string") return error;
  return error.message || "Error";
};

export const useRequirementsLoadsQuery = (params) =>
  useQuery({
    queryKey: requirementsLoadsQueryKey(params),
    queryFn: ({ signal }) => fetchRequirementsLoads(params, signal),
    enabled: params != null,
    retry: false,
    refetchOnWindowFocus: false,
  });

export const useTruckFilterLoadsQuery = ({ onNewLoads } = {}) => {
  const query = useQuery({
    queryKey: TRUCK_FILTER_LOADS_KEY,
    queryFn: ({ signal }) => fetchTruckFilterLoads(signal),
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: true,
    staleTime: STALE_TIME_MS,
  });

  const queryClient = useQueryClient();
  const ingestTruckFilterLoads = useLoadFinderStore((s) => s.ingestTruckFilterLoads);
  const skipNotificationsOnceRef = useRef(false);
  const lastIngestedAtRef = useRef(0);

  const { data, dataUpdatedAt } = query;
  useEffect(() => {
    if (!data || dataUpdatedAt === lastIngestedAtRef.current) return;
    lastIngestedAtRef.current = dataUpdatedAt;
    const newLoadsByFilter = ingestTruckFilterLoads(data, { skipNotifications: skipNotificationsOnceRef.current });
    skipNotificationsOnceRef.current = false;
    if (Object.keys(newLoadsByFilter).length > 0) onNewLoads?.(newLoadsByFilter);
  }, [data, dataUpdatedAt, ingestTruckFilterLoads, onNewLoads]);

  const refresh = useCallback(
    ({ skipNotifications = false } = {}) => {
      if (skipNotifications) skipNotificationsOnceRef.current = true;
      return queryClient.invalidateQueries({ queryKey: TRUCK_FILTER_LOADS_KEY });
    },
    [queryClient],
  );

  return { ...query, refresh };
};
